import {
  formatCurrencyDecimal,
  parseCurrencyDecimal,
} from "utils/currencyUtils";

import { filterPropertyAlias, handlePasteWithoutAccents } from "@/utils/utils";
import type { AddressFormData } from "@/types/address";

import { AddressForm } from "js/components/Address";
import { Input } from "js/components/ui/input";
import { Label } from "js/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "js/components/ui/select";
import { Separator } from "js/components/ui/separator";

import { PROPERTY_USE_OPTIONS } from "./constants";
import { PropertyFormData } from "./hooks/usePropertyForm";
import { PropertyFieldKey } from "js/utils/propertyValidation";

interface PropertyFormStepProps {
  formData: PropertyFormData;
  updateFormData: (updates: Partial<PropertyFormData>) => void;
  getFieldError: (field: PropertyFieldKey) => string | undefined;
  setFieldTouched: (field: PropertyFieldKey) => void;
  touchedFields: Partial<Record<PropertyFieldKey, boolean>>;
  isEditMode?: boolean;
}

export const PropertyFormStep = ({
  formData,
  updateFormData,
  getFieldError,
  setFieldTouched,
  touchedFields,
  isEditMode = false,
}: PropertyFormStepProps) => {
  // Convert PropertyFormData address fields to AddressFormData
  const addressFormData: AddressFormData = {
    street: formData.street || "",
    exterior_number: formData.exterior_number || "",
    interior_number: formData.interior_number || "",
    neighborhood: formData.colonia || "", // Map colonia -> neighborhood
    city: formData.city || "",
    municipality: formData.municipality || "",
    state: formData.state || "",
    zip_code: formData.zip_code || "",
  };

  // Map AddressFormData field names to PropertyFieldKey names for blur tracking
  const handleAddressBlur = (field: keyof AddressFormData) => {
    const fieldMap: Partial<Record<keyof AddressFormData, PropertyFieldKey>> = {
      street: "street",
      exterior_number: "exterior_number",
      interior_number: "interior_number",
      neighborhood: "colonia",
      city: "city",
      state: "state",
      zip_code: "zip_code",
    };
    const mappedField = fieldMap[field];
    if (mappedField) {
      setFieldTouched(mappedField);
    }
  };

  // Handle address changes from AddressForm
  const handleAddressChange = (data: AddressFormData) => {
    updateFormData({
      street: data.street,
      exterior_number: data.exterior_number,
      interior_number: data.interior_number,
      colonia: data.neighborhood, // Map neighborhood -> colonia
      municipality: data.municipality,
      city: data.city,
      state: data.state,
      zip_code: data.zip_code,
    });
  };

  // Convert address errors for AddressForm
  const addressErrors = {
    street: getFieldError("street"),
    exterior_number: getFieldError("exterior_number"),
    interior_number: getFieldError("interior_number"),
    neighborhood: getFieldError("colonia"), // Map colonia -> neighborhood
    city: getFieldError("city"),
    municipality: undefined,
    state: getFieldError("state"),
    zip_code: getFieldError("zip_code"),
  };

  // Transform function matching existing filterStreetInput behavior
  const transformValue = (value: string) =>
    value
      .toUpperCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  const handleAmountChange = (
    field: "rental_price" | "maintenance_price",
    value: string,
  ) => {
    const numericValue = parseCurrencyDecimal(value);
    updateFormData({ [field]: numericValue });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row gap-4">
        {!isEditMode && (
          <div className="md:w-1/4">
            <Label className="text-sm font-medium text-gray-700 mb-1 block">
              Uso que le dará al inmueble *
            </Label>
            <Select
              value={formData.uso}
              onValueChange={(v) => {
                updateFormData({ uso: v });
                setFieldTouched("uso");
              }}
            >
              <SelectTrigger
                className={`border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900 w-full ${
                  getFieldError("uso") ? "border-red-500" : ""
                }`}
              >
                <SelectValue placeholder="Uso del inmueble" />
              </SelectTrigger>
              <SelectContent>
                {PROPERTY_USE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {getFieldError("uso") && (
              <p className="text-sm text-red-600 mt-1">
                {getFieldError("uso")}
              </p>
            )}
          </div>
        )}
        <div className={isEditMode ? "w-full" : "md:w-3/4"}>
          <Label className="text-sm font-medium text-gray-700 mb-1 block">
            Alias del inmueble
          </Label>
          <Input
            value={formData.alias}
            onChange={(e) => {
              const filtered = filterPropertyAlias(e.target.value);
              updateFormData({
                alias: filtered,
              });
            }}
            onPaste={(e) => {
              handlePasteWithoutAccents(e, filterPropertyAlias, (value) => {
                updateFormData({ alias: value });
              });
            }}
            onBlur={() => setFieldTouched("alias")}
            placeholder="Alias del inmueble"
            className={`border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900 ${
              getFieldError("alias") ? "border-red-500" : ""
            }`}
            maxLength={50}
          />
          {getFieldError("alias") && (
            <p className="text-sm text-red-600 mt-1">
              {getFieldError("alias")}
            </p>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-sm font-medium text-gray-700 mb-1 block">
            Valor mensual de la renta *
          </Label>
          <div className="relative">
            <Input
              type="text"
              placeholder="$12,000.00"
              value={formatCurrencyDecimal(formData.rental_price)}
              onChange={(e) =>
                handleAmountChange("rental_price", e.target.value)
              }
              onBlur={() => setFieldTouched("rental_price")}
              className={`border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900 pr-12 ${
                getFieldError("rental_price") ? "border-red-500" : ""
              }`}
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <span className="text-gray-500 text-sm font-medium">MXN</span>
            </div>
          </div>
          {getFieldError("rental_price") && (
            <p className="text-sm text-red-600 mt-1">
              {getFieldError("rental_price")}
            </p>
          )}
        </div>
        <div>
          <Label className="text-sm font-medium text-gray-700 mb-1 block">
            Valor mensual del mantenimiento
          </Label>
          <div className="relative">
            <Input
              type="text"
              placeholder="$3,000.00"
              value={formatCurrencyDecimal(formData.maintenance_price)}
              onChange={(e) =>
                handleAmountChange("maintenance_price", e.target.value)
              }
              onBlur={() => setFieldTouched("maintenance_price")}
              className={`border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900 pr-12 ${
                getFieldError("maintenance_price") ? "border-red-500" : ""
              }`}
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <span className="text-gray-500 text-sm font-medium">MXN</span>
            </div>
          </div>
          {getFieldError("maintenance_price") && (
            <p className="text-sm text-red-600 mt-1">
              {getFieldError("maintenance_price")}
            </p>
          )}
        </div>
      </div>

      <Separator className="my-6" />

      <AddressForm
        value={addressFormData}
        onChange={handleAddressChange}
        onBlur={handleAddressBlur}
        errors={addressErrors}
        touched={{
          street: !!touchedFields.street,
          exterior_number: !!touchedFields.exterior_number,
          interior_number: !!touchedFields.interior_number,
          neighborhood: !!touchedFields.colonia,
          city: !!touchedFields.city,
          municipality: false,
          state: !!touchedFields.state,
          zip_code: !!touchedFields.zip_code,
        }}
        title="Dirección"
        transformValue={transformValue}
      />
    </div>
  );
};
